import React, {useEffect, useRef, useState} from 'react';
import {Notification} from '../navigation/page-layout';
import {RegionId, ServiceResource, useAuth} from '../hooks/use-auth';
import {translateErrorToReactNode} from '../common';
import PortalLoader from '../common/PortalLoader';
import * as QuickSightEmbedding from 'amazon-quicksight-embedding-sdk';
import '../../assets/styles/forecast-styles.scss';
import {AppLayout, ButtonDropdown, ContentLayout, HelpPanel, Link} from '@amzn/awsui-components-react';
import {useGetDashboardUrlByDashboardName} from '../hooks/use-quicksight-api';

const REGION_OPTIONS = [
    {id: 'NA', text: 'NA'},
    {id: 'EU', text: 'EU'},
    {id: 'IN', text: 'IN'},
    {id: 'FE', text: 'FE'},
];

export function ViewRuntimeTrackerDashboard(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();
    const [selectedRegion, setSelectedRegion] = useState(RegionId.NA);

    const clientConfigs = auth.authInformation!.getEndpoints()[selectedRegion][ServiceResource.ForecastStoreInsights];

    function createErrorListener(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const {status: getDashboardUrlStatus, value: getDashboardUrlResponse} = useGetDashboardUrlByDashboardName(
        clientConfigs,
        {dashboardName: 'Runtime Tracker'},
        true,
        createErrorListener('GetDashboardUrlByDashboardName failed'),
        [auth, selectedRegion]
    );

    const isLoading = getDashboardUrlStatus === 'idle' || getDashboardUrlStatus === 'pending';

    return (
        <AppLayout
            disableContentPaddings={true}
            content={
                <ContentLayout>
                    <ButtonDropdown items={REGION_OPTIONS} onItemClick={({detail}) => setSelectedRegion(detail.id as RegionId)}>
                        Region: {selectedRegion}
                    </ButtonDropdown>
                    {isLoading ? (
                        <PortalLoader />
                    ) : (
                        <RuntimeTrackerDashboard
                            key={selectedRegion}
                            url={getDashboardUrlResponse?.dashboardUrl || ''}
                            pushNotification={props.pushNotification}
                        />
                    )}
                </ContentLayout>
            }
            tools={
                <HelpPanel header="Runtime Improvements Design Doc">
                    <p>
                        <Link
                            href="https://quip-amazon.com/UARLAuh106hd/SOP-Performance-Improvements"
                            external
                            externalIconAriaLabel="Opens in a new tab"
                        >
                            Learn more about Excelsior Performance Improvements
                        </Link>
                    </p>
                </HelpPanel>
            }
            navigationHide
        />
    );
}

interface RuntimeTrackerDashboardProps {
    url: string;
    pushNotification: (notification: Notification) => void;
}

function RuntimeTrackerDashboard(props: RuntimeTrackerDashboardProps) {
    const ref = useRef<HTMLDivElement | null>(null);

    function onError(error: any) {
        props.pushNotification({
            type: 'error',
            header: 'Embedding Quicksight Dashboard failed',
            content: translateErrorToReactNode(error),
        });
    }

    useEffect(() => {
        const dashboard = QuickSightEmbedding.embedDashboard({
            url: props.url,
            container: ref.current || '',
            locale: 'en-US',
            footerPaddingEnabled: true,
            parameters: {},
        });
        dashboard.on('error', onError);
    }, []);

    return <div id="embeddingContainer" ref={ref} />;
}
